// The WebAssembly core: the SAME C++ that the desktop studio and the world
// generator use -- schematic parsing and writing, the geometry ops, the block
// model baker and the in-world name generator. Nothing in this app
// reimplements any of that in TypeScript; a second implementation would drift
// from the generator the moment either side changed.
//
// The module is produced by the CMake target `studio_core` (emscripten) into
// public/wasm/. Until it has been built, `loadCore()` fails with a message
// saying exactly that, and the UI stays usable for everything that does not
// need geometry.

export interface SchematicInfo {
  handle: number;
  sx: number;
  sy: number;
  sz: number;
  placed: number;
  air: number;
  voidAir: number;
  blockEntities: number;
  // Bedrock cells: not blocks but "guarantee solid ground here" marks. The
  // export keeps whatever the terrain generated and fills only air or water.
  groundMarks: number;
  states: number;
}

export interface MeshOverlay {
  // Belongs to the rule, not the view, but the core only holds the schematic.
  bury?: boolean;
  yOffset: number;
  ground: boolean;
  footprint: boolean;
  grid: boolean;
  markSunken: boolean;
  showFoundation: boolean;
  sliceTop: number; // -1 = whole build
}

// One draw batch. The vertex layout matches the desktop renderer exactly
// (position, uv, colour, texture flag), so both front ends run the same shader
// and a build looks identical in the browser and in the app.
export interface MeshData {
  solid: Float32Array;
  overlayTris: Float32Array;
  overlayLines: Float32Array;
  ghost: Float32Array;
}

export interface Atlas {
  width: number;
  height: number;
  pixels: Uint8Array; // RGBA8
}

export interface NameKind {
  id: string;
  label: string;
  words: string[];
  defaults: string[];
}

export interface NamePlaceholder {
  token: string;
  description: string;
  example: string;
}

export interface StudioCore {
  // --- structures ---------------------------------------------------------
  loadSchematic(bytes: Uint8Array): SchematicInfo;
  freeSchematic(handle: number): void;
  buildMesh(handle: number, overlay: MeshOverlay): MeshData;

  // Geometry edits return NEW .schem bytes, ready to be uploaded; the caller
  // reloads from those bytes so what is shown is always what was saved.
  rotate(handle: number, steps: number): Uint8Array;
  mirror(handle: number, alongX: boolean): Uint8Array;
  trim(handle: number): { bytes: Uint8Array; removed: number[] };
  shiftAnchor(handle: number, dx: number, dz: number): Uint8Array;

  // The loaded build as ".schem" bytes. loadSchematic also reads Minecraft's
  // structure-block ".nbt"; this is how such an import reaches the project in
  // the one format everything else stores and reads.
  toSchem(handle: number): Uint8Array;

  // --- assets -------------------------------------------------------------
  // Feed the Minecraft client jar once; the core unpacks the block models and
  // bakes a texture atlas. Returns false when the jar carries no block assets
  // (a server jar, typically).
  setAssets(jar: Uint8Array): boolean;
  atlas(): Atlas | null;
  assetsReady(): boolean;
  unresolvedStates(): number;

  // --- names --------------------------------------------------------------
  nameExamples(spec: string, count: number, seed: number): string[];
  generateName(spec: string, seed: number): string;
  // The vocabularies a build can draw on, each with its words and its default
  // patterns -- so the browser editor offers exactly what the desktop one does.
  nameKinds(): NameKind[];
  namePlaceholders(): NamePlaceholder[];
  validateNamePattern(pattern: string): { ok: boolean; error: string };
}

let cached: Promise<StudioCore> | null = null;

export function coreAvailable(): boolean {
  return cached !== null;
}

export async function loadCore(): Promise<StudioCore> {
  if (cached) return cached;
  cached = (async () => {
    // Emitted by the CMake target `studio_core` (emscripten) as an ES module.
    // The path is absolute so Vite serves it straight from public/ instead of
    // trying to bundle emscripten's glue.
    let factory: (opts?: Record<string, unknown>) => Promise<StudioCore>;
    try {
      // The path is a runtime URL, not a module TypeScript can resolve: the
      // glue is emitted by the C++ build into public/wasm/ and served as-is.
      const url = "/wasm/studio_core.js";
      const mod = (await import(/* @vite-ignore */ url)) as {
        default: typeof factory;
      };
      factory = mod.default;
    } catch {
      throw new Error(
        "The WebAssembly core is missing. Build it with:\n" +
          "  PATH=/usr/lib/emscripten:$PATH emcmake cmake -S web/wasm -B build-wasm\n" +
          "  cmake --build build-wasm",
      );
    }
    return factory();
  })();
  return cached;
}
